// «Клиенты»: список (сортировка, поиск), вкладка «Группы» (порядок, свёрнутые), архив; карточка клиента.
import { GROUP_KIND, KEY_KINDS, KEY_LABEL, MEET, MSG_TOKEN, RU_DAYS, RU_DAYS_FULL, STATUS, calTitle, dowOf, effColorKey,
  effectiveRules, hasValue, isColor, isCommercial, isDateStr, isHeld, money, normSig, plural, priceFor, ruDate,
  ruDateShort, tgLink, tzCity, waLink, type ClientOut, type GroupKind, type GroupOut, type KeyKind } from '@praktika/core';
import { afterChange, call, reload, rpc } from '../lib/api.ts';
import { colName, colorOf, colorSheet, cdot, colorCss, colv } from '../lib/colors.ts';
import { $, $$, esc, store, toast } from '../lib/dom.ts';
import { activeTypes, balPill, defaultGroup, defaultType, groupById, groupOf, meetLabel, payerOf, statusCls,
  statusLabel, toMain, typeById, typeName } from '../lib/data.ts';
import { SCREENS, fab, go, render, replace, title } from '../lib/nav.ts';
import { chooseSheet, closeSheet, curSheet, kvBtn, kvTog, openSheet, opt, promptSheet } from '../lib/sheet.ts';
import { S, UI, type ClientDraft } from '../lib/state.ts';
import { nowInfo, tzDiffLabel, tzSheet } from '../lib/zones.ts';
import { bindCommon, meetSheet, remind } from './common.ts';
import { MESSENGERS, chooseRule, clientInfo, cntClients, lastMove, ruleText, sortCodes } from './parts.ts';

export const CSORTS: Array<[string, string]> = [['name', 'по имени'], ['next', 'ближайшая встреча'],
  ['last', 'давно не были'], ['count', 'больше сессий'], ['price', 'по цене'], ['debt', 'по долгу'],
  ['new', 'недавно добавлены']];
UI.csort = store('praktika_csort') || 'name';

function sortLabel(v: string): string { return (CSORTS.find((x) => x[0] === v) ?? CSORTS[0]!)[1]; }

export function clientSorter(mode = UI.csort || 'name'): (a: ClientOut, b: ClientOut) => number {
  const today = nowInfo().date, next: Record<string, string> = {}, last: Record<string, string> = {};
  for (const x of S.sessions) {
    if (!isHeld(x.status)) continue;
    if (x.date >= today) { if (!next[x.clientId] || x.date < next[x.clientId]!) next[x.clientId] = x.date; }
    else if (!last[x.clientId] || x.date > last[x.clientId]!) last[x.clientId] = x.date;
  }
  const byName = (a: ClientOut, b: ClientOut) => a.name.localeCompare(b.name);
  const cmp = (x: string, y: string) => (x < y ? -1 : x > y ? 1 : 0);
  if (mode === 'next') return (a, b) => cmp(next[a.id] || '9999', next[b.id] || '9999') || byName(a, b);
  if (mode === 'last') return (a, b) => cmp(last[a.id] || '0000', last[b.id] || '0000') || byName(a, b);
  if (mode === 'count') return (a, b) => (b.doneTotal || 0) - (a.doneTotal || 0) || byName(a, b);
  if (mode === 'price') return (a, b) => toMain(b.effPrice || 0, b.effCur) - toMain(a.effPrice || 0, a.effCur) || byName(a, b);
  if (mode === 'debt') return (a, b) => toMain(a.balance || 0, a.balanceCur) - toMain(b.balance || 0, b.balanceCur) ||
    byName(a, b);
  if (mode === 'new') return (a, b) => cmp(b.created || '', a.created || '') || byName(a, b);
  return byName;
}

document.addEventListener('click', (e) => {
  if (!(e.target as HTMLElement).closest?.('[data-csort]')) return;
  chooseSheet('Сортировка клиентов', CSORTS.map((x) => ({ value: x[0], label: x[1] })), UI.csort || 'name', (v) => {
    UI.csort = v;
    store('praktika_csort', v);
    render();
  });
});

// Метка группы у клиента в списке: название (кроме «Личных» по умолчанию).
function groupTag(c: ClientOut): string {
  const g = groupOf(c);
  return g && !g.isDefault ? ' <span class="tag ' + (isCommercial(g) ? 'p' : 'l') + '">' + esc(g.name) + '</span>' : '';
}

// Группы на вкладке: в порядке с сервера; архивная — только если в ней есть действующие клиенты.
function groupKeys(active: ClientOut[]): GroupOut[] {
  return S.groups.filter((g) => !g.archived || active.some((c) => c.groupId === g.id));
}

// Порядок и свёрнутые группы: сразу на экране, на сервер — без сообщения; при ошибке — снова с сервера.
function groupsViewQuiet(v: { order?: string[]; collapsed?: string[] }): void {
  if (v.collapsed) for (const g of S.groups) g.collapsed = v.collapsed.includes(g.id);
  if (v.order) {
    const pos = new Map(v.order.map((id, i) => [id, i]));
    S.groups.sort((a, b) => (pos.get(a.id) ?? 1e6) - (pos.get(b.id) ?? 1e6));
  }
  render();
  rpc('groupsView', v).catch((e: Error) => { toast(e.message, true); void reload(); });
}

function toggleCollapsed(id: string): void {
  const cl = S.groups.filter((g) => g.collapsed !== (g.id === id)).map((g) => g.id);
  groupsViewQuiet({ collapsed: cl });
}

function groupsHtml(active: ClientOut[]): string {
  const sorter = clientSorter(S.settings.grp_sort || 'name');
  const dot = (k: string) => '<i class="cdot" style="background:' + colorCss(k).dot + '"></i>';
  const row = (c: ClientOut, pill: boolean) => {
    const mv = lastMove(c);
    return '<button class="gr" data-client="' + esc(c.id) + '"><div class="who"><div class="n">' + cdot(c) + esc(c.name) +
      '</div><div class="i">' + esc(clientInfo(c)) + '</div>' + (mv ? '<div class="i mv">' + esc(mv) + '</div>' : '') +
      '</div>' + (pill || c.balance ? balPill(c.balance, c.balanceCur) : '') + '</button>';
  };
  return groupKeys(active).map((g) => {
    const cl = active.filter((c) => c.groupId === g.id).sort(sorter), comm = isCommercial(g), shut = g.collapsed;
    let debt = 0;
    for (const c of cl) if ((c.balance ?? 0) < 0) debt += toMain(-(c.balance ?? 0), c.balanceCur);
    const head = '<div class="who"><div class="n">' + dot(effColorKey(null, g, S.settings)) + esc(g.name) +
      ' <span class="tag ' + (comm ? 'p">проект' : 'l">личная') + '</span></div><div class="i">' +
      (hasValue(g.price) ? esc(money(g.price, g.cur)) + ' · ' : '') + cntClients(cl.length) +
      (comm ? ' · ' + (g.pays === 'monthly' ? 'раз в месяц' : 'за сессию') :
        (debt ? ' · долги ' + esc(money(Math.round(debt), S.settings.main_currency)) : '')) + '</div></div>' +
      (comm ? balPill(g.balance, g.cur) : '');
    return '<div class="grp' + (shut ? ' closed' : '') + '"><div class="grph"><button class="gopen" data-gopen="' +
      esc(g.id) + '">' + head + '</button><button class="gtog" data-gtog="' + esc(g.id) + '" aria-label="' +
      (shut ? 'Развернуть' : 'Свернуть') + '">' + (shut ? '▸' : '▾') + '</button></div>' +
      (cl.map((c) => row(c, !comm)).join('') || '<div class="gr none">Клиентов пока нет</div>') + '</div>';
  }).join('');
}

// Окно «Группы»: перетащить за ≡ — порядок; переключатель — показывать клиентов группы или только шапку.
function groupsSheet(active: ClientOut[]): void {
  const sh = openSheet('<div class="ttl">Группы</div><p class="sub">Перетащите группу за ≡, чтобы поставить её выше ' +
    'или ниже. Переключатель — показывать клиентов группы или только её шапку.</p><div class="glist"></div>');
  let keys = groupKeys(active).map((g) => g.id);
  const draw = () => {
    $('.glist', sh).innerHTML = keys.map((k) => {
      const g = groupById(k)!;
      return '<div class="grow" data-k="' + esc(k) + '"><span class="gh" aria-label="Перетащить">≡</span><span class="zn"><b>' +
        esc(g.name) + '</b><span class="small">' + cntClients(active.filter((c) => c.groupId === k).length) +
        '</span></span><button class="tog' + (g.collapsed ? '' : ' on') + '" data-gk="' + esc(k) +
        '" aria-label="Показывать клиентов"></button></div>';
    }).join('');
    $$('[data-gk]', sh).forEach((t) => { t.onclick = () => { toggleCollapsed(t.getAttribute('data-gk')!); draw(); }; });
    $$('.gh', sh).forEach((h) => {
      h.onpointerdown = (e) => {
        e.preventDefault();
        const row = h.parentElement!, list = row.parentElement!;
        let y0 = e.clientY;
        try { h.setPointerCapture(e.pointerId); } catch { /* старый браузер */ }
        row.classList.add('drag');
        h.onpointermove = (ev) => {
          const y = ev.clientY, nx = row.nextElementSibling as HTMLElement | null,
            pv = row.previousElementSibling as HTMLElement | null;
          if (nx && y - y0 > nx.offsetHeight / 2) { list.insertBefore(nx, row); y0 += nx.offsetHeight; }
          else if (pv && y0 - y > pv.offsetHeight / 2) { list.insertBefore(row, pv); y0 -= pv.offsetHeight; }
          row.style.transform = 'translateY(' + (y - y0) + 'px)';
        };
        h.onpointerup = h.onpointercancel = () => {
          h.onpointermove = h.onpointerup = h.onpointercancel = null;
          row.classList.remove('drag');
          row.style.transform = '';
          const order = $$('.grow', sh).map((r) => r.getAttribute('data-k')!);
          if (order.join('\n') !== keys.join('\n')) { keys = order; groupsViewQuiet({ order }); }
        };
      };
    });
  };
  draw();
}

// Новая группа: сначала вид — личная (клиенты платят сами) или коммерческий проект (платит организация).
function newGroupSheet(): void {
  chooseSheet<GroupKind | ''>('Новая группа', [{ value: GROUP_KIND.PERSONAL, label: 'Личная группа',
    hint: 'клиенты платят сами, у каждого свой счёт · например «хПК» или «Студенты»' }, { value: GROUP_KIND.PROJECT,
    label: 'Коммерческий проект', hint: 'за клиентов платит организация, счёт общий' }], '', (v) => {
    if (!v) return;
    const project = v === GROUP_KIND.PROJECT;
    UI.draftGroup = { name: '', kind: v, cur: S.settings.main_currency, typeId: project ? defaultType().id : null,
      pays: 'monthly', payDay: project ? 5 : null, selfReceipt: false, meet: project ? MEET.BY_GROUP : '', link: '',
      messenger: project ? 'WhatsApp' : '', price: null, inn: '', bankNames: '', contact: '', note: '', color: '',
      ruleHours: null, ruleLate: null, ruleNoShow: null, calCodes: [] };
    go('group', { id: '' });
  });
}

// Переезд клиента в другую группу с какого-то дня. Сессии с этого дня (и уже отмеченные) переходят туда же: кто платит,
// цена и списание — по новой группе; более ранние остаются как были. Та же группа — только перенести сессии с этого дня.
function groupMoveSheet(c: ClientOut): void {
  const list = S.groups.filter((g) => !g.archived || g.id === c.groupId);
  chooseSheet('Группа', list.map((g) => ({ value: g.id, label: g.name, hint: isCommercial(g) ? 'проект · платит организация' :
    'личная · клиент платит сам' })), c.groupId, (gid) => {
    const g = groupById(gid);
    if (!g) return;
    const today = nowInfo().date, same = gid === c.groupId;
    const cnt = (d: string) => {
      const xs = S.sessions.filter((x) => x.clientId === c.id && x.date >= d && x.groupId !== gid);
      const marked = xs.filter((x) => x.status !== STATUS.PLAN).length;
      return xs.length ? 'перейдут сессии: ' + xs.length + (marked ? ', из них отмеченных ' + marked : '') :
        'сессий для переноса нет';
    };
    const next = S.sessions.filter((x) => x.clientId === c.id && x.date > today && x.status === STATUS.PLAN)
      .map((x) => x.date).sort()[0];
    const opts = [{ d: today, label: 'С сегодняшнего дня · ' + ruDateShort(today) }];
    if (next) opts.push({ d: next, label: 'Со следующей сессии · ' + ruDateShort(next) });
    const sh = openSheet('<div class="ttl">' + esc(same ? 'Перенести сессии в «' + g.name + '»' : c.name + ' → ' + g.name) +
      '</div><p class="sub">' + (same ? esc(c.name) + ' уже в «' + esc(g.name) + '». Сессии с выбранного дня, которые ' +
      'остались в другой группе, перейдут в «' + esc(g.name) + '».' : 'С какого дня? Сессии с этого дня перейдут в «' +
      esc(g.name) + '»: кто платит, цена и списание — по новой группе. Более ранние останутся как были. В карточке ' +
      'клиента останется запись о переезде.') + '</p>' + opts.map((o) => opt('', '', o.label, cnt(o.d))).join('') +
      opt('', '', 'С другого дня…', 'например, с первой встречи после перехода'));
    const run = (d: string) => {
      closeSheet();
      void call<{ moved: number }>('moveClient', c.id, gid, d).then((r) => afterChange(same ? 'В «' + g.name +
        '» перенесено сессий: ' + r.moved : c.name + ' → «' + g.name + '» с ' + ruDateShort(d) +
        (r.moved ? ' · сессий ' + r.moved : '')), () => {});
    };
    $$('button.opt', sh).forEach((b, i) => {
      b.onclick = () => {
        if (i < opts.length) run(opts[i]!.d);
        else { closeSheet(); promptSheet('С какого дня', today, 'date', (d) => { if (isDateStr(d)) run(d); }); }
      };
    });
  });
}

SCREENS.clients = (app) => {
  const seg = UI.clientSeg, q = normSig(UI.search);
  const personal = S.clients.filter((c) => !c.archived), archive = S.clients.filter((c) => c.archived);
  const ng = S.groups.length;
  let html = title('Клиенты', personal.length + ' ' + plural(personal.length, 'клиент', 'клиента', 'клиентов') + ' · ' +
    ng + ' ' + plural(ng, 'группа', 'группы', 'групп'), true) +
    '<div class="search"><input placeholder="Поиск" data-q value="' + esc(UI.search) + '"></div>' +
    (seg === 'projects' ? '<div class="sortrow two"><button data-gset>Настроить группы</button>' +
      '<button data-gsort>Сортировка: ' + esc(sortLabel(S.settings.grp_sort || 'name')) + ' ▾</button></div>' :
      '<div class="sortrow"><button data-csort>Сортировка: ' + esc(sortLabel(UI.csort)) + ' ▾</button></div>') +
    '<div class="seg"><button' + (seg === 'personal' ? ' class="on"' : '') + ' data-seg="personal">Клиенты</button>' +
    '<button' + (seg === 'projects' ? ' class="on"' : '') + ' data-seg="projects">Группы</button><button' +
    (seg === 'archive' ? ' class="on"' : '') + ' data-seg="archive">Архив</button></div><div class="p-list' +
    (seg === 'projects' ? ' groups' : '') + '">';
  if (seg === 'projects') html += groupsHtml(personal);
  else {
    const list = (seg === 'archive' ? archive : personal).filter((c) => !q || normSig(c.name).includes(q) ||
      normSig(c.bankNames).includes(q)).sort(clientSorter());
    html += list.map((c) => {
      const payer = payerOf(c);
      return '<button class="p-row cl" data-client="' + esc(c.id) + '"><div class="who"><div class="n">' + cdot(c) +
        esc(c.name) + groupTag(c) + (c.review === 'auto' ? ' <span class="tag o">проверить</span>' : '') +
        '</div><div class="i">' + esc(clientInfo(c)) + '</div></div>' +
        (payer && payer.isProject && !c.balance ? '<span class="bal zero">счёт проекта</span>' :
          balPill(c.balance, c.balanceCur)) + '</button>';
    }).join('') || '<div class="empty">' + (q ? 'Никого не нашлось' : 'Пока пусто') + '</div>';
  }
  html += '</div>';
  app.innerHTML = html;
  const qi = $<HTMLInputElement>('[data-q]', app);
  qi.oninput = () => {
    UI.search = qi.value;
    const v = normSig(qi.value), gl = $('.p-list.groups', app);
    if (gl) gl.classList.toggle('q', !!v);        // при поиске свёрнутые группы раскрываются
    $$('[data-client]', app).forEach((r) => { r.style.display = !v || normSig(r.textContent).includes(v) ? '' : 'none'; });
  };
  $$('[data-seg]', app).forEach((b) => {
    b.onclick = () => { UI.clientSeg = b.getAttribute('data-seg') as typeof UI.clientSeg; render(); };
  });
  $$('[data-gtog]', app).forEach((t) => { t.onclick = () => toggleCollapsed(t.getAttribute('data-gtog')!); });
  const gset = $('[data-gset]', app);
  if (gset) gset.onclick = () => groupsSheet(personal);
  const gsort = $('[data-gsort]', app);
  if (gsort) gsort.onclick = () => chooseSheet('Сортировка клиентов в группах', CSORTS.map((x) => ({ value: x[0],
    label: x[1] })), S.settings.grp_sort || 'name', (v) => {
    S.settings.grp_sort = v;
    render();
    rpc('saveSettings', { grp_sort: v }).catch((e: Error) => { toast(e.message, true); void reload(); });
  });
  $$('[data-client]', app).forEach((b) => { b.onclick = () => go('client', { id: b.getAttribute('data-client') }); });
  $$('[data-gopen]', app).forEach((b) => { b.onclick = () => go('group', { id: b.getAttribute('data-gopen') }); });
  fab(seg === 'projects' ? 'Группа' : 'Клиент', () => {
    if (seg === 'projects') { newGroupSheet(); return; }
    UI.draftClient = { name: '', callName: '', groupId: S.groupDefault, typeId: defaultType().id || null, price: null,
      cur: S.settings.main_currency, meet: S.settings.meet_kind_default, link: '', makeMeet: S.settings.meet_default,
      address: '', tz: S.tz, slotDay: null, slotTime: '', contact: '', messenger: 'WhatsApp', bankNames: '',
      ownRules: false, ruleHours: null, ruleLate: null, ruleNoShow: null, note: '', color: '' };
    go('client', { id: '' });
  });
};

// Цена по правилу и откуда она: своя, группы, типа сессии.
function priceLabel(c: ClientDraft & { effTypeId?: string | null }, g: GroupOut | null): string {
  const r = priceFor(c, g, typeById(c.effTypeId || g?.typeId || c.typeId), S.settings.main_currency);
  if (r.price === null) return 'не задана';
  return money(r.price, r.cur) + (r.from === 'client' ? (g && hasValue(g.price) ? ' · своя, не группы' : '') :
    r.from === 'group' ? ' · цена группы' : ' · по типу сессии');
}

// Как приложение запишет встречу: код — свой у клиента, если он у его группы; иначе код группы; иначе свой.
function calPreview(c: ClientOut): string {
  const g = groupOf(c), own = c.calCode || '', codes = g ? sortCodes(g.calCodes) : [];
  return calTitle(own && codes.includes(own) ? own : (codes[0] || own), c.calName || [c.name, MSG_TOKEN[c.messenger] || '']
    .filter(Boolean).join(' '), c.calNoNum ? '' : (c.doneTotal || 0) + 1, c.calTail || '');
}

function calFormatSheet(c: ClientOut, save: (p: Record<string, unknown>) => void): void {
  const inp = (attr: string, v: string, ph: string) => '<input ' + attr + ' value="' + esc(v) + '" placeholder="' + esc(ph) +
    '" style="flex:1;text-align:right;border:0;outline:0;font-weight:500;background:transparent">';
  const sh = openSheet('<div class="ttl">Как писать в календарь</div>' +
    '<p class="sub">Название события собирается так: код, имя, номер сессии, приписка. Номер приложение подставляет само.</p>' +
    '<div class="kv"><span class="k">Код</span>' + inp('data-a', c.calCode, 'ИК') + '</div>' +
    '<div class="kv"><span class="k">Имя в календаре</span>' + inp('data-b', c.calName, c.name) + '</div>' +
    '<div class="kv"><span class="k">Приписка после номера</span>' + inp('data-c', c.calTail, 'онлайн') + '</div>' +
    '<div class="btnrow" style="padding:12px 0 0"><button class="btn pri" data-ok>Сохранить</button></div>');
  $('[data-ok]', sh).onclick = () => {
    const v = (a: string) => $<HTMLInputElement>(a, sh).value.trim();
    const p = { calCode: v('[data-a]'), calName: v('[data-b]'), calTail: v('[data-c]') };
    closeSheet();
    save(p);
  };
}

// Приметы клиента: по чему приложение узнаёт его во встречах календаря и в выписках. Кроме них — имя целиком.
const KEY_HINT: Record<KeyKind, string> = { phone: 'он же WhatsApp · в любой записи: +7…, 8…, 0…', tg: '@ник или ссылка t.me/…',
  email: 'если приглашаете клиента во встречу', link: 'постоянная ссылка Zoom или Meet', cal: 'как вы пишете его в названии встречи',
  bank: 'как банк подписывает его переводы' };
const KEY_INPUT: Record<KeyKind, 'tel' | 'text' | 'email' | 'url'> = { phone: 'tel', tg: 'text', email: 'email', link: 'url',
  cal: 'text', bank: 'text' };

function keysCard(c: ClientOut): string {
  const byKind = KEY_KINDS.map((k) => [k, c.keys.filter((x) => x.kind === k)] as const).filter(([, l]) => l.length);
  return '<div class="card"><div class="hd">Как узнаём клиента</div><p class="p-note" style="padding:4px 14px 8px">' +
    'Во встречах календаря и в выписках приложение находит клиента по имени целиком и по этим приметам. Примета, ' +
    'которая есть и у другого клиента, — «общая»: по ней не узнаём, а спрашиваем.</p>' +
    byKind.map(([k, list]) => '<div class="kv col"><span class="k">' + esc(KEY_LABEL[k]) + '</span><div class="sig">' +
      list.map((x) => '<span>' + esc(x.shown) + (x.shared ? ' · общая' : '') + '<button data-key-rm="' + esc(x.id) +
        '" aria-label="Удалить">×</button></span>').join('') + '</div></div>').join('') +
    '<button class="kv act" data-key-add><span class="k">+ Добавить примету</span><span class="v chev"></span></button></div>';
}

function addKeySheet(c: ClientOut): void {
  chooseSheet<KeyKind>('Примета', KEY_KINDS.map((k) => ({ value: k, label: KEY_LABEL[k], hint: KEY_HINT[k] })), 'phone',
    (kind) => promptSheet(KEY_LABEL[kind], '', KEY_INPUT[kind], (v) => {
      const raw = String(v || '').trim();
      if (!raw) return;
      rpc('addClientKey', c.id, kind, raw).then(() => afterChange('Примета добавлена'), (e: Error) => {
        if (!/Уже есть у клиента/.test(e.message)) { toast(e.message, true); return; }
        if (confirm(e.message + '\n\nДобавить как общее?')) {
          void call('addClientKey', c.id, kind, raw, true).then(() => afterChange('Добавлено как общее'), () => {});
        }
      });
    }));
}

// Карточка клиента (существующий или черновик нового).
SCREENS.client = (app, p) => {
  const isNew = !p.id, ex = isNew ? null : S.clients.find((x) => x.id === p.id) ?? null;
  const c: ClientDraft | null = isNew ? UI.draftClient ?? null : ex;
  if (!c) { app.innerHTML = title('Клиент') + '<div class="empty">Не найден</div>'; return; }
  const g = groupById(c.groupId) ?? (isNew ? (defaultGroup() as GroupOut) : null);   // группа клиента (любого вида)
  const payer = ex ? payerOf(ex) : null, cur = ex ? ex.effCur || ex.cur : c.cur;
  const rules = ex ? ex.rules : effectiveRules(c, g, S.settings);
  const grpRules = !!g && (hasValue(g.ruleHours) || hasValue(g.ruleLate) || hasValue(g.ruleNoShow));
  const save = (patch: Record<string, unknown>) => {
    if (!ex) { Object.assign(c, patch); render(); return; }
    void call('saveClient', { ...patch, id: ex.id }).then(() => afterChange('Сохранено'), () => {});
  };
  let html = '<div class="p-title"><button class="back" data-back>‹</button><span class="t">' +
    (isNew ? 'Новый клиент' : esc(c.name)) + '</span>' + (ex && payer ? (payer.isProject && !ex.balance ?
    '<span class="bal zero">счёт ' + esc(payer.name) + '</span>' : balPill(ex.balance, ex.balanceCur)) : '') + '</div>';
  if (ex) html += '<div class="btnrow"><button class="btn pri" data-book-client>Записать</button>' +
    '<button class="btn sec" data-write>Написать</button></div>';
  html += '<div class="card">' + kvBtn('Имя', c.name, 'data-f="name"') + kvBtn('Как обращаться', c.callName,
    'data-f="callName"') +
    kvBtn('Группа', g ? g.name + (isCommercial(g) ? ' · проект' : ' · личная') : '', 'data-group') +
    (ex && ex.moves.length ? '<div class="kv col"><span class="k">Переезды</span><span class="v small">' +
      ex.moves.map((m) => esc(ruDateShort(m.date) + ': ' + m.fromName + ' → ' + m.toName)).join('<br>') + '</span></div>' : '') +
    (ex && payer && payer.isProject ? '<div class="kv"><span class="k">Платит</span><span class="v">проект «' +
      esc(payer.name) + '»</span></div>' : '') +
    kvBtn('Тип сессии', g && g.typeId ? typeName(g.typeId) + ' · от группы' : typeName(c.typeId), 'data-type') +
    kvBtn('Цена', priceLabel(c, g), 'data-price') +
    kvBtn('Встреча', meetLabel(c), 'data-meet') + (c.meet === MEET.MEET ? kvTog('Создавать Meet', c.makeMeet,
      'data-tog="makeMeet"') : '') +
    kvBtn('Часовой пояс', tzCity(c.tz) + (c.tz && c.tz !== S.tz ? ' · ' + tzDiffLabel(c.tz) : ''), 'data-tz') +
    kvBtn('Постоянный слот', c.slotDay !== null ? RU_DAYS[c.slotDay]!.toLowerCase() + ' ' + c.slotTime : 'нет', 'data-slot') +
    '<button class="kv" data-color><span class="k">Цвет в расписании</span><span class="v chev">' +
      colv(colorOf(c)) + (isColor(c.color) ? '' : ' · как у всех') + '</span></button>' +
    kvBtn('Контакт', c.contact, 'data-f="contact" data-type="tel"') + kvBtn('Мессенджер', c.messenger, 'data-msgr') +
    kvBtn('Заметка', c.note, 'data-f="note" data-type="textarea"') +
    '</div>';
  if (ex) html += '<div class="card"><div class="hd">Счётчик сессий</div>' + kvBtn('Проведено всего',
    String(ex.doneTotal || 0), 'data-done') + (S.settings.cal_title_coded ? kvBtn('В календаре пишется', calPreview(ex),
    'data-calfmt') : '') + '</div>' + keysCard(ex);
  html += '<div class="card"><div class="hd">Правила отмены' + (grpRules ? ' · по умолчанию из группы' : '') + '</div>' +
    kvTog('Свои правила', c.ownRules, 'data-tog="ownRules"') +
    kvBtn('Порог «вовремя»', (c.ownRules ? (c.ruleHours || rules.hours) : rules.hours) + ' ч',
      c.ownRules ? 'data-rule="hours"' : 'disabled', c.ownRules ? '' : 'small') +
    kvBtn('Отмена поздно', ruleText(c.ownRules && hasValue(c.ruleLate) ? c.ruleLate : rules.late, cur),
      c.ownRules ? 'data-rule="late"' : 'disabled') +
    kvBtn('Не пришёл', ruleText(c.ownRules && hasValue(c.ruleNoShow) ? c.ruleNoShow : rules.noshow, cur),
      c.ownRules ? 'data-rule="noshow"' : 'disabled') + '</div>';
  if (!ex) html += '<div class="btnrow"><button class="btn pri" data-create' + (c.name ? '' : ' disabled') +
    '>Сохранить</button></div>';
  else {
    const allHist = S.sessions.filter((x) => x.clientId === ex.id).sort((a, b) => (a.date + a.start) < (b.date + b.start) ?
      1 : -1), hist = UI.histAll === ex.id ? allHist : allHist.slice(0, 12);
    const pays = S.payments.filter((x) => x.payerId === ex.id).sort((a, b) => a.date < b.date ? 1 : -1).slice(0, 8);
    html += '<div class="list-h">Сессии · в приложении ' + ex.nSessions + (ex.doneTotal ? ' · проведено всего ' +
      ex.doneTotal : '') + '</div><div class="p-list">' + (hist.map((s) => '<button class="p-row" data-sess-open="' +
      esc(s.id) + '"><div class="tm">' + esc(s.start) + '</div><div class="who"><div class="n">' +
      esc(RU_DAYS[dowOf(s.date)] + ' ' + ruDate(s.date)) + '</div><div class="i">' + (s.num ? '№' + s.num + ' · ' : '') +
      esc(typeName(s.typeId)) + ' ' + esc(s.dur) + ' · ' + esc(money(s.price, s.cur)) +
      (s.charged && s.status !== STATUS.DONE ? ' · начислено ' + esc(money(s.charged, s.cur)) : '') +
      '</div></div><div class="st ' + statusCls(s.status) + '">' + esc(statusLabel(s.status)) + '</div></button>').join('') ||
      '<div class="empty">Сессий ещё не было</div>') + '</div>' +
      (allHist.length > hist.length ? '<div class="btnrow" style="margin-top:8px"><button class="btn sec" data-hist-all>' +
        'Показать все — ' + allHist.length + '</button></div>' : '');
    if (!(payer && payer.isProject) || pays.length) html += '<div class="list-h">Оплаты</div><div class="p-list">' +
      (pays.map((o) => '<div class="p-row pay"><div class="who"><div class="n">' + esc(ruDateShort(o.date)) +
      '</div><div class="i">' + esc(o.source) + (o.note ? ' · ' + esc(o.note) : '') + '</div></div><div class="amt pos">+' +
      esc(money(o.amount, o.cur)) + '</div></div>').join('') || '<div class="empty">Оплат ещё не было</div>') + '</div>';
    html += '<div class="btnrow" style="margin-top:12px"><button class="btn txt" data-archive>' +
      (ex.archived ? 'Вернуть из архива' : 'В архив') + '</button></div>';
  }
  app.innerHTML = html;
  bindCommon(app);
  if (ex) {
    const more = $('[data-hist-all]', app);
    if (more) more.onclick = () => { UI.histAll = ex.id; render(); };
    $('[data-book-client]', app).onclick = () => go('book', { clientId: ex.id });
    $('[data-write]', app).onclick = () => {
      const nx = S.sessions.filter((x) => x.clientId === ex.id && x.status === STATUS.PLAN && x.date >= nowInfo().date)
        .sort((a, b) => (a.date + a.start) < (b.date + b.start) ? -1 : 1)[0];
      if (nx) remind(nx.id);
      else if (ex.contact) window.open(ex.messenger === 'Telegram' ? tgLink(ex.contact, '') : waLink(ex.contact, ''), '_blank');
      else toast('Контакт не указан', true);
    };
  }
  $$('[data-f]', app).forEach((b) => {
    b.onclick = () => {
      const f = b.getAttribute('data-f') as 'name' | 'callName' | 'contact' | 'note';
      promptSheet($('.k', b).textContent ?? '', c[f], (b.getAttribute('data-type') || 'text') as 'text',
        (v) => save({ [f]: v }));
    };
  });
  // группа: у нового клиента — просто выбрать; у существующего — переезд с какого-то дня (сессии переходят тоже)
  $('[data-group]', app).onclick = () => {
    if (ex) { groupMoveSheet(ex); return; }
    chooseSheet('Группа', S.groups.filter((x) => !x.archived).map((x) => ({ value: x.id, label: x.name,
      hint: isCommercial(x) ? 'проект · платит организация' : 'личная · клиент платит сам' })), c.groupId, (v) => {
      const ng = groupById(v);
      c.groupId = v;
      if (isCommercial(ng)) c.meet = MEET.BY_GROUP;
      else if (c.meet === MEET.BY_GROUP) c.meet = S.settings.meet_kind_default;
      render();
    });
  };
  $('[data-type]', app).onclick = () => chooseSheet('Тип сессии', activeTypes().map((t) => ({ value: t.id, label: t.name,
    hint: t.dur + ' мин · перерыв ' + t.brk })), c.typeId ?? '', (v) => save({ typeId: v }));
  const dn = $('[data-done]', app);
  if (dn && ex) dn.onclick = () => promptSheet('Проведено сессий всего', ex.doneTotal || 0, 'number', (v) => {
    const t = Math.max(0, Math.round(Number(v) || 0));
    save({ sessionsBefore: Math.max(0, t - (ex.doneApp || 0)) });
  }, 'Приложение считает само: состоявшиеся сессии и оплаченные отмены плюс то, что было до него. Здесь можно поправить итог.');
  const cf = $('[data-calfmt]', app);
  if (cf && ex) cf.onclick = () => calFormatSheet(ex, save);
  if (ex) {
    $('[data-key-add]', app).onclick = () => addKeySheet(ex);
    $$('[data-key-rm]', app).forEach((b) => {
      b.onclick = () => {
        const k = ex.keys.find((x) => x.id === b.getAttribute('data-key-rm'));
        if (!k || !confirm('Удалить примету «' + k.shown + '»?')) return;
        void call('removeClientKey', k.id).then(() => afterChange('Примета удалена'), () => {});
      };
    });
  }
  $('[data-price]', app).onclick = () => promptSheet('Цена за сессию', c.price, 'number', (v) => {
    curSheet('Валюта', c.cur || S.settings.main_currency, (cu) => save({ price: v === '' ? null : Number(v), cur: cu }));
  });
  $('[data-meet]', app).onclick = () => {
    const tmp = { meet: c.meet, link: c.link, address: c.address };
    meetSheet(tmp, c, () => save({ meet: tmp.meet, link: tmp.link, address: tmp.address,
      makeMeet: tmp.meet === MEET.MEET ? true : c.makeMeet }));
  };
  $$('[data-tog]', app).forEach((b) => {
    b.onclick = () => { const f = b.getAttribute('data-tog') as 'makeMeet' | 'ownRules'; save({ [f]: !c[f] }); };
  });
  $('[data-tz]', app).onclick = () => tzSheet(c.tz, (v) => save({ tz: v }));
  $('[data-color]', app).onclick = () => colorSheet({ title: 'Цвет клиента',
    sub: 'Так выделяются его сессии в расписании, точка в списках и событие в календаре Google.', current: c.color,
    kind: 'client', none: 'Как у всех', noneHint: colName(effColorKey(null, g, S.settings)).toLowerCase() +
      (g && isColor(g.color) ? ' · цвет группы' : ' · по умолчанию') }, (v) => save({ color: v }));
  $('[data-slot]', app).onclick = () => chooseSheet<number | null>('День слота', [{ value: null,
    label: 'Без постоянного слота' }, ...RU_DAYS.map((_, i) => ({ value: i, label: RU_DAYS_FULL[i]! }))], c.slotDay, (d) => {
    if (d === null) { save({ slotDay: null, slotTime: '' }); return; }
    promptSheet('Время', c.slotTime || '10:00', 'time', (t) => save({ slotDay: d, slotTime: t }));
  });
  $('[data-msgr]', app).onclick = () => chooseSheet('Мессенджер', MESSENGERS, c.messenger, (v) => save({ messenger: v }));
  $$('[data-rule]', app).forEach((b) => {
    b.onclick = () => {
      const r = b.getAttribute('data-rule');
      if (r === 'hours') promptSheet('Порог «вовремя», часов', c.ruleHours || rules.hours, 'number',
        (v) => save({ ruleHours: Number(v) || 24 }));
      else chooseRule(r === 'late' ? 'Отмена поздно, оплачивается' : 'Не пришёл, оплачивается',
        r === 'late' ? c.ruleLate : c.ruleNoShow, cur, (v) => save({ [r === 'late' ? 'ruleLate' : 'ruleNoShow']: v }));
    };
  });
  const cr = $('[data-create]', app);
  if (cr) cr.onclick = () => void call<{ id: string }>('saveClient', c).then((r) => {
    UI.draftClient = null;
    return afterChange('Клиент добавлен').then(() => replace('client', { id: r.id }));
  }, () => {});
  const ar = $('[data-archive]', app);
  if (ar && ex) ar.onclick = () => save({ archived: !ex.archived });
};
